import { computed, watch, reactive, ref, Ref } from "@vue/composition-api"
import { menuTree } from "../menuTree"
import { filterMenuList, formatMenuList } from "@/utils"
import {
  Privilege,
  UserSetting,
  MenuItemType,
  FlatMenuObject,
  FormatedMenuItemType,
} from "../../types"
import { checkAndPermission } from "@/utils/permission"
import { useAuth } from "@/use/biz/use-auth"
import { useUserInfoState } from "@/use/biz/use-userInfo"
import { useSidebarUnread } from "./use-unread"
import * as storage from "@shared/utils/storage/local"
import * as PRIVILEGE from "@shared/constants/privilege"
import { UserInfoState } from "@/layout/types"

const getShowBadgeIdsConfig = () => {
  const { checkPermission } = useAuth()
  const userInfoState = useUserInfoState() as Ref<UserInfoState>

  //lite版本主账号显示红点，主账号admin_type = 2
  const liteBadgeShow =
    !checkPermission(PRIVILEGE.mktFunctionalSiteCustom) &&
    userInfoState.value.admin_type === 2

  return [
    {
      id: "inquiryManagement",
      roles: [PRIVILEGE.mktInquiryView],
      condition: liteBadgeShow,
    },
    {
      id: "formManagement",
      roles: [PRIVILEGE.mktFormView],
      condition: liteBadgeShow,
    },
    {
      id: "settingsRoles",
      roles: [PRIVILEGE.mktSettingRoleManage],
      condition: liteBadgeShow,
    },
    {
      id: "settingsRecord",
      roles: [PRIVILEGE.mktSettingOperateRecord],
      condition: liteBadgeShow,
    },
  ]
}

const BADGE_STATE_KEY = "mktMenuBadgeStateKey"

export const useBadgeAndActivity = (privilege: Privilege) => {
  let showBadgeIds = ref<string[]>(
    getShowBadgeIdsConfig()
      .filter(
        (item) => checkAndPermission(item.roles, privilege) && item.condition,
      )
      .map((item) => item.id),
  )
  let showActivityIds = ref<string[]>([])

  const updateBadgeStorage = (menu: FormatedMenuItemType) => {
    const parentMenu = menu.parentMenu[0]

    if (showBadgeIds.value.includes(parentMenu)) {
      storage.put(`${BADGE_STATE_KEY}-${parentMenu}`, "1")
    }

    if (showBadgeIds.value.includes(menu.id)) {
      showBadgeIds.value = showBadgeIds.value.filter((id) => id !== menu.id)
      storage.put(`${BADGE_STATE_KEY}-${menu.id}`, "1")
    }
  }

  return {
    showBadgeIds,
    showActivityIds,
    updateBadgeStorage,
  }
}

export const useMenuList = (privilege: Privilege, setting: UserSetting) => {
  const {
    sidebarUnreadMap,
    fetchSidebarAllUnread,
    fetchSidebarUnread,
  } = useSidebarUnread()

  const {
    showBadgeIds,
    showActivityIds,
    updateBadgeStorage,
  } = useBadgeAndActivity(privilege)

  const dealModules = (list: MenuItemType[]) => {
    return list.map((item) => {
      const {
        subMenu,
      }: {
        id: string
        subMenu?: MenuItemType[]
      } = item

      const newSubMenu =
        subMenu &&
        subMenu.map((sub) => {
          // 子菜单显示红点
          const showBadge =
            showBadgeIds.value.includes(sub.id) &&
            !storage.get(`${BADGE_STATE_KEY}-${sub.id}`)

          return {
            showBadge,
            ...sub,
            activity: showActivityIds.value.includes(sub.id),
          }
        })

      // 主菜单显示红点
      const showBadge =
        (showBadgeIds.value.includes(item.id) &&
          !storage.get(`${BADGE_STATE_KEY}-${item.id}`)) ||
        newSubMenu?.some((item) => item?.showBadge)

      return {
        ...item,
        subMenu: newSubMenu,
        showBadge,
      }
    })
  }

  const menuList = computed(() => {
    return formatMenuList({
      menuList: filterMenuList({
        menuList: dealModules(menuTree),
        privilege,
        setting,
      }),
      sidebarUnreadMap,
    })
  })

  const getFlatMenu = <T extends MenuItemType, FormatedMenuItemType>(
    list: Readonly<T[]> | undefined,
  ): FlatMenuObject => {
    return list
      ? list.reduce((obj: FlatMenuObject, next: T) => {
          return {
            ...obj,
            [next.id]: next,
            ...getFlatMenu(next.subMenu),
          }
        }, {})
      : {}
  }

  let flatMenuObject: FlatMenuObject = {}
  watch(
    menuList,
    (val) => {
      flatMenuObject = reactive(getFlatMenu(val))
    },
    {
      immediate: true,
    },
  )

  return {
    menuList,
    flatMenuObject,
    fetchSidebarAllUnread,
    fetchSidebarUnread,
    updateBadgeStorage,
  }
}
